import { useEffect, useState } from 'react';

export enum ScriptStatus {
  Idle = 'idle',
  Loading = 'loading',
  Ready = 'ready',
  Error = 'error',
}

type StatusListener = (status: ScriptStatus) => void;

const cachedScripts: Record<string, HTMLScriptElement> = {};
const scriptStatusBySrc: Record<string, ScriptStatus> = {};
const statusListenersBySrc: Record<string, Set<StatusListener>> = {};

const isFinalStatus = (status: ScriptStatus | undefined) =>
  status === ScriptStatus.Ready || status === ScriptStatus.Error;

const createScript = (src: string) => {
  const script = document.createElement('script');

  script.src = src;
  script.async = true;

  const handleScriptEvent = (event: Event) => {
    const status = event.type === 'load' ? ScriptStatus.Ready : ScriptStatus.Error;

    scriptStatusBySrc[src] = status;
    statusListenersBySrc[src]?.forEach((listener) => listener(status));
  };

  script.addEventListener('load', handleScriptEvent);
  script.addEventListener('error', handleScriptEvent);

  document.body.appendChild(script);
  cachedScripts[src] = script;
  scriptStatusBySrc[src] = ScriptStatus.Loading;

  return script;
};

const useScript = (src: string) => {
  const [status, setStatus] = useState<ScriptStatus>(
    src ? scriptStatusBySrc[src] || ScriptStatus.Loading : ScriptStatus.Idle,
  );

  useEffect(() => {
    if (!src) {
      setStatus(ScriptStatus.Idle);

      return;
    }

    if (!cachedScripts[src]) {
      createScript(src);
    }

    const currentStatus = scriptStatusBySrc[src];

    setStatus(currentStatus);

    if (isFinalStatus(currentStatus)) {
      return;
    }

    statusListenersBySrc[src] ??= new Set();

    const listeners = statusListenersBySrc[src];

    listeners.add(setStatus);

    return () => {
      listeners.delete(setStatus);
    };
  }, [src]);

  return status;
};

export default useScript;
